import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addChallengesFromPool,
  createChallenge,
  deleteChallenge,
  listOwnerChallenges,
  reorderChallenges,
  updateChallenge,
  updateEvent,
} from '../api/owner'
import type { OwnerChallenge } from '../api/ownerTypes'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Spinner } from '../components/icons'
import { plural } from '../lib/format'
import { useEvent } from './eventContext'
import { EventScreen } from './components/EventScreen'
import { ChallengeForm } from './challenges/ChallengeForm'
import { ChallengePoolDialog } from './challenges/ChallengePoolDialog'
import { kindLabel, momentLabel } from './challenges/kind'

/**
 * Ronde 22A en 22G — challenges beheren.
 *
 * Op mobiel een lijst met greep, op desktop een tabel met kolommen. Beide met
 * de globale schakelaar erboven: staat die uit, dan bestaat het woord challenge
 * niet in het gastenportaal.
 *
 * Ordenen gaat met pijltjes en niet met slepen, net als bij de foto's op de
 * landing. Slepen vecht op een telefoon met het scrollen van de pagina, en de
 * volgorde is hier bovendien geen fijnmazige keuze — het gaat om wat bovenaan
 * staat.
 */
export function EventChallengesPage() {
  const { subject } = useEvent()
  const queryClient = useQueryClient()

  const [pool, setPool] = useState(false)
  const [form, setForm] = useState<{ open: boolean; existing: OwnerChallenge | null }>({
    open: false,
    existing: null,
  })
  const [removing, setRemoving] = useState<OwnerChallenge | null>(null)
  const [error, setError] = useState<string | null>(null)

  const key = ['owner', 'challenges', subject.id]

  const challenges = useQuery({ queryKey: key, queryFn: () => listOwnerChallenges(subject.id) })

  const refresh = () => queryClient.invalidateQueries({ queryKey: key })

  const run = useMutation({
    mutationFn: async (task: () => Promise<unknown>) => task(),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : 'Dat lukte niet. Probeer nog eens.'),
  })

  const list = challenges.data?.challenges ?? []
  const enabled = challenges.data?.challengesEnabled ?? true
  const visible = list.filter((challenge) => challenge.isVisible).length

  function move(index: number, direction: -1 | 1) {
    const next = [...list]
    const target = index + direction

    if (target < 0 || target >= next.length) {
      return
    }

    ;[next[index], next[target]] = [next[target], next[index]]

    run.mutate(() =>
      reorderChallenges(
        subject.id,
        next.map((challenge) => challenge.id),
      ),
    )
  }

  return (
    <>
      <EventScreen
        title="Challenges"
        eventTitle={`${list.length} in de lijst · ${visible} aan`}
        desktopSubtitle="Kies uit de pool of maak er zelf een. De volgorde bepaal je met de pijltjes."
        aside={
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={() => setPool(true)}
              className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-12 rounded-full border px-5 text-[15px] transition-colors"
            >
              Uit de pool kiezen
            </button>
            <button
              type="button"
              onClick={() => setForm({ open: true, existing: null })}
              className="bg-accent text-accent-ink h-12 rounded-full px-6 text-[15px] font-semibold transition hover:brightness-95"
            >
              Zelf maken
            </button>
          </div>
        }
      >
        {challenges.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner className="text-accent size-6" />
          </div>
        ) : (
          <div className="lg:max-w-[900px]">
            {/* --- De globale schakelaar --------------------------------- */}
            <div className="rounded-notice border-line-card bg-surface mb-5 flex items-center gap-4 border p-4 lg:p-4.5">
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold lg:text-base">Challenges aan</p>
                <p className="text-ink-soft mt-0.5 text-[13px] lg:text-sm">
                  {enabled
                    ? 'Gasten zien de tab en kunnen meedoen.'
                    : 'Gasten zien er niets van; de tab is weg.'}
                </p>
              </div>

              <Switch
                on={enabled}
                label="Challenges aan"
                onChange={(value) =>
                  run.mutate(async () => {
                    await updateEvent(subject.id, { challengesEnabled: value })
                    await queryClient.invalidateQueries({
                      queryKey: ['owner', 'event', subject.id],
                    })
                  })
                }
              />
            </div>

            {error && (
              <p role="alert" className="text-accent mb-4 text-sm">
                {error}
              </p>
            )}

            {list.length === 0 ? (
              <div className="rounded-card border-line-card bg-surface border p-8 text-center">
                <p className="font-display text-[26px] leading-tight">Nog geen challenges</p>
                <p className="text-ink-soft mx-auto mt-2 max-w-[380px] text-sm leading-[1.55] text-pretty">
                  Kies er een paar uit de pool, of verzin er zelf een. Gasten zien ze in een eigen
                  tab naast de galerij.
                </p>

                <button
                  type="button"
                  onClick={() => setPool(true)}
                  className="bg-accent text-accent-ink mt-6 h-12 rounded-full px-6 text-[15px] font-semibold transition hover:brightness-95"
                >
                  Uit de pool kiezen
                </button>
              </div>
            ) : (
              <>
                <p className="text-ink-faint mb-3 text-[11px] tracking-[0.18em] uppercase lg:hidden">
                  In de lijst
                </p>

                {/* --- Mobiel: kaarten ---------------------------------- */}
                <div className="flex flex-col gap-2 lg:hidden">
                  {list.map((challenge, index) => (
                    <ChallengeRow
                      key={challenge.id}
                      challenge={challenge}
                      first={index === 0}
                      last={index === list.length - 1}
                      onMove={(direction) => move(index, direction)}
                      onEdit={() => setForm({ open: true, existing: challenge })}
                      onToggle={(value) =>
                        run.mutate(() =>
                          updateChallenge(subject.id, challenge.id, { isVisible: value }),
                        )
                      }
                    />
                  ))}
                </div>

                {/* --- Desktop: tabel ----------------------------------- */}
                <div className="rounded-card border-line-card bg-surface hidden overflow-hidden border lg:block">
                  <div className="text-ink-faint bg-band flex items-center gap-4 px-5 py-3.5 text-xs tracking-[0.14em] uppercase">
                    <div className="w-14" />
                    <div className="flex-1">Opdracht</div>
                    <div className="w-24">Type</div>
                    <div className="w-32">Moment</div>
                    <div className="w-24 text-right">Inzendingen</div>
                    <div className="w-20 text-right">Zichtbaar</div>
                  </div>

                  {list.map((challenge, index) => (
                    <div
                      key={challenge.id}
                      className="border-line-menu flex items-center gap-4 border-t px-5 py-3.5"
                    >
                      <Handle
                        first={index === 0}
                        last={index === list.length - 1}
                        onMove={(direction) => move(index, direction)}
                      />

                      <button
                        type="button"
                        onClick={() => setForm({ open: true, existing: challenge })}
                        className="hover:text-accent min-w-0 flex-1 text-left text-base font-medium transition-colors"
                      >
                        {challenge.title}
                      </button>

                      <div className="text-ink-soft w-24 text-[15px]">
                        {kindLabel(challenge.kind)}
                      </div>
                      <div className="text-ink-soft w-32 text-[15px]">
                        {momentLabel(challenge.unlocksAtUtc)}
                      </div>
                      <div className="w-24 text-right text-base">{challenge.submissionCount}</div>

                      <div className="flex w-20 justify-end">
                        <Switch
                          on={challenge.isVisible}
                          label={`${challenge.title} zichtbaar`}
                          onChange={(value) =>
                            run.mutate(() =>
                              updateChallenge(subject.id, challenge.id, { isVisible: value }),
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Op mobiel staan de twee knoppen onderaan (22A). */}
            <div className="mt-5 flex gap-2.5 lg:hidden">
              <button
                type="button"
                onClick={() => setPool(true)}
                className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-13 flex-1 rounded-full border text-[15px] transition-colors"
              >
                Uit de pool
              </button>
              <button
                type="button"
                onClick={() => setForm({ open: true, existing: null })}
                className="bg-accent text-accent-ink h-13 flex-1 rounded-full text-[15px] font-semibold transition hover:brightness-95"
              >
                Zelf maken
              </button>
            </div>
          </div>
        )}
      </EventScreen>

      <ChallengePoolDialog
        open={pool}
        taken={list.map((challenge) => challenge.title)}
        busy={run.isPending}
        onClose={() => setPool(false)}
        onCustom={() => {
          setPool(false)
          setForm({ open: true, existing: null })
        }}
        onAdd={(ids) =>
          run.mutate(async () => {
            await addChallengesFromPool(subject.id, ids)
            setPool(false)
          })
        }
      />

      {form.open && (
        <ChallengeForm
          // De sleutel dwingt een verse state per challenge; anders blijft de
          // vorige titel in het formulier staan als je een andere opent.
          key={form.existing?.id ?? 'nieuw'}
          open
          existing={form.existing}
          eventDateUtc={subject.eventDateUtc}
          busy={run.isPending}
          onClose={() => setForm({ open: false, existing: null })}
          onDelete={form.existing ? () => setRemoving(form.existing) : undefined}
          onSave={(body) =>
            run.mutate(async () => {
              if (form.existing) {
                await updateChallenge(subject.id, form.existing.id, {
                  ...body,
                  clearUnlocksAt: body.unlocksAtUtc === null,
                })
              } else {
                await createChallenge(subject.id, body)
              }

              setForm({ open: false, existing: null })
            })
          }
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Challenge verwijderen?"
        detail={
          removing && removing.submissionCount > 0
            ? `${plural(removing.submissionCount, 'foto', "foto's")} blijft gewoon in de galerij staan, alleen de opdracht verdwijnt.`
            : 'De opdracht verdwijnt uit het portaal van je gasten.'
        }
        confirmLabel="Ja, verwijderen"
        cancelLabel="Laat staan"
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const target = removing

          setRemoving(null)
          setForm({ open: false, existing: null })

          if (target) {
            run.mutate(() => deleteChallenge(subject.id, target.id))
          }
        }}
      />
    </>
  )
}

/** Eén challenge op mobiel (22A). */
function ChallengeRow({
  challenge,
  first,
  last,
  onMove,
  onEdit,
  onToggle,
}: {
  challenge: OwnerChallenge
  first: boolean
  last: boolean
  onMove: (direction: -1 | 1) => void
  onEdit: () => void
  onToggle: (value: boolean) => void
}) {
  return (
    <div className="rounded-notice border-line-card bg-surface flex items-center gap-3 border p-3 px-3.5">
      <Handle first={first} last={last} onMove={onMove} />

      <button type="button" onClick={onEdit} className="min-w-0 flex-1 text-left">
        <span className="block truncate text-[15px] font-medium">{challenge.title}</span>
        <span className="text-ink-faint mt-0.5 block text-[13px]">
          {kindLabel(challenge.kind)}
          {challenge.unlocksAtUtc
            ? ` · ${momentLabel(challenge.unlocksAtUtc).toLowerCase()}`
            : challenge.guestCount > 0
              ? ` · ${plural(challenge.guestCount, 'gast', 'gasten')}`
              : ''}
        </span>
      </button>

      <Switch on={challenge.isVisible} label={`${challenge.title} zichtbaar`} onChange={onToggle} />
    </div>
  )
}

/** De greep uit het ontwerp, hier als twee knoppen. */
function Handle({
  first,
  last,
  onMove,
}: {
  first: boolean
  last: boolean
  onMove: (direction: -1 | 1) => void
}) {
  return (
    <span className="flex shrink-0 flex-col">
      <button
        type="button"
        aria-label="Naar boven"
        disabled={first}
        onClick={() => onMove(-1)}
        className="text-ink-ghost hover:text-ink px-1.5 text-[11px] leading-none transition-colors disabled:opacity-30 disabled:hover:text-current"
      >
        ▲
      </button>
      <button
        type="button"
        aria-label="Naar beneden"
        disabled={last}
        onClick={() => onMove(1)}
        className="text-ink-ghost hover:text-ink px-1.5 text-[11px] leading-none transition-colors disabled:opacity-30 disabled:hover:text-current"
      >
        ▼
      </button>
    </span>
  )
}

function Switch({
  on,
  label,
  onChange,
}: {
  on: boolean
  label: string
  onChange: (value: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className={
        'relative h-6.5 w-11 shrink-0 rounded-full transition-colors ' +
        (on ? 'bg-accent' : 'bg-line')
      }
    >
      <span
        aria-hidden="true"
        className={
          'absolute top-0.75 size-5 rounded-full bg-white shadow-sm transition-[left] ' +
          (on ? 'left-5.25' : 'left-0.75')
        }
      />
    </button>
  )
}
